/**
 * 파일 보관함 전용 콤팩트 업로드 바.
 *
 * 기존 DragDropZone 이 큰 박스를 세로로 차지하던 문제를 해결 — 한 줄짜리 슬림한 바로
 * 압축하여 시각적 비중을 줄이고 본문(파일 목록) 영역에 더 많은 공간을 양보한다.
 *
 * 기능은 유지:
 *  - 드래그 앤 드롭 (바 전체가 drop 영역)
 *  - 파일 / 폴더 선택 버튼
 *  - 검증(파일 형식 / 크기)
 *  - 업로드 진행 상태(LinearProgress)
 *
 * 사용처: `ProjectFileRepositoryView` 만. parking-test 등 다른 곳에서 쓰는 큰
 * DragDropZone 은 그대로 둔다.
 */

import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import type { FileCategory } from '../../models/FileStorage';
import { ChunkUploader } from '../../utils/ChunkUploader';

interface CompactUploadBarProps {
  category: FileCategory;
  /** 검증을 통과한 파일이 부모로 전달됨. */
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  uploading?: boolean;
  uploadProgress?: number;
  /** 활성 카테고리의 accent 색 — 드래그 하이라이트 / 진행률 막대 색에 사용. */
  accent: string;
}

const isFolderCategory = (category: FileCategory): boolean =>
  category === 'learning' || category === 'test';

const CompactUploadBar: React.FC<CompactUploadBarProps> = ({
  category,
  onFilesSelected,
  disabled = false,
  uploading = false,
  uploadProgress = 0,
  accent,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [isDragging, setIsDragging] = useState(false);

  const acceptedExtensions = ChunkUploader.getAcceptedExtensions(category);
  const categoryLabel = ChunkUploader.getCategoryLabel(category);
  const isFolderUpload = isFolderCategory(category);
  const isDisabled = disabled || uploading;

  const handleFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const { valid, invalid } = ChunkUploader.validateFiles(files, category);
      if (invalid.length > 0) {
        const errorMessages = invalid
          .slice(0, 10)
          .map(({ file, error }) => `${file.name}: ${error}`);
        const moreCount =
          invalid.length > 10 ? `\n\n... 외 ${invalid.length - 10}개 파일` : '';
        // eslint-disable-next-line no-alert
        alert(`파일 검증 실패:\n\n${errorMessages.join('\n')}${moreCount}`);
      }
      if (valid.length > 0) {
        onFilesSelected(valid);
      }
    },
    [category, onFilesSelected],
  );

  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      handleFiles(files);
      event.target.value = '';
    },
    [handleFiles],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDisabled) setIsDragging(true);
    },
    [isDisabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (isDisabled) return;
      handleFiles(Array.from(e.dataTransfer.files));
    },
    [handleFiles, isDisabled],
  );

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: '1px dashed',
        borderColor: isDragging
          ? accent
          : isDark
            ? alpha('#fff', 0.18)
            : alpha('#000', 0.18),
        bgcolor: isDragging
          ? alpha(accent, isDark ? 0.18 : 0.1)
          : isDark
            ? alpha('#1a1a1a', 0.55)
            : alpha('#fff', 0.9),
        transition: 'border-color 160ms, background-color 160ms',
        opacity: isDisabled ? 0.7 : 1,
        '&:hover': {
          borderColor: isDisabled
            ? undefined
            : isDragging
              ? accent
              : alpha(accent, 0.6),
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accent,
          bgcolor: alpha(accent, isDark ? 0.18 : 0.12),
        }}
      >
        <CloudUploadIcon fontSize="small" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {uploading ? (
          <Box>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{
                height: 6,
                borderRadius: 3,
                '& .MuiLinearProgress-bar': { bgcolor: accent },
              }}
            />
            <Typography
              variant="caption"
              sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}
            >
              업로드 중 · {uploadProgress}%
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary' }}>
              {isFolderUpload
                ? '폴더를 끌어다 놓거나 우측 버튼으로 선택'
                : '파일을 끌어다 놓거나 우측 버튼으로 선택'}
            </Typography>
            <Typography
              sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {categoryLabel} · {acceptedExtensions} · 최대 1GB
            </Typography>
          </Box>
        )}
      </Box>

      <input
        type="file"
        multiple
        accept={acceptedExtensions}
        onChange={handleFileInput}
        disabled={isDisabled}
        style={{ display: 'none' }}
        id={`compact-file-input-${category}`}
        {...(isFolderUpload
          ? ({ webkitdirectory: '', directory: '' } as Record<string, string>)
          : {})}
      />
      <label htmlFor={`compact-file-input-${category}`} style={{ flexShrink: 0 }}>
        <Button
          variant="contained"
          component="span"
          disabled={isDisabled}
          startIcon={<CloudUploadIcon />}
          size="small"
          sx={{
            bgcolor: accent,
            color: '#fff',
            boxShadow: 'none',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: alpha(accent, 0.85), boxShadow: 'none' },
          }}
        >
          {isFolderUpload ? '폴더 선택' : '파일 업로드'}
        </Button>
      </label>
    </Box>
  );
};

export default CompactUploadBar;
